import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// PRD-008 F15 / AC-13 — visual differentiation for the operator
// console. Slate-gray top bar, "Platform Operator" badge in place of
// the workspace switcher, operator email top-right, separate sidebar
// nav (Dashboard, Workspaces, Audit Log).
//
// Calibrated against the Stafftools / Stripe-internal reference: the
// top bar is the at-a-glance signal that "you are not in the customer
// product." The body content keeps a light background so dense data
// tables stay readable.
const NAV = [
  { name: 'Dashboard', href: '/internal/dashboard' },
  { name: 'Workspaces', href: '/internal/workspaces' },
  { name: 'Audit Log', href: '/internal/audit' },
];

export default function InternalLayout({ children, pageTitle }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar — slate-gray, distinct from the customer product blue. */}
      <header className="bg-slate-700 text-white">
        <div className="px-6 h-14 flex items-center">
          <span className="font-semibold text-sm tracking-wide">Verdict</span>
          <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded bg-slate-600 text-xs font-medium uppercase tracking-wider">
            Platform Operator
          </span>
          <div className="flex-1" />
          <span className="text-sm text-slate-200">{user?.email}</span>
          <button
            onClick={handleLogout}
            className="ml-4 text-xs text-slate-300 hover:text-white"
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar — operator-specific nav, no workspace switcher. */}
        <aside className="w-56 min-h-[calc(100vh-3.5rem)] bg-white border-r border-gray-200">
          <nav className="px-3 py-4">
            <ul className="space-y-1">
              {NAV.map((item) => {
                const isActive = location.pathname.startsWith(item.href);
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className={`block px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-slate-100 text-slate-900'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {item.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        <main className="flex-1 px-6 py-6">
          {pageTitle && (
            <h1 className="text-xl font-semibold text-gray-900 mb-4">{pageTitle}</h1>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
